import { FormProvider, useForm, useFormContext } from "react-hook-form"
import isEmail from "validator/lib/isEmail"
import isMobilePhone from "validator/lib/isMobilePhone"
import isPostalCode from "validator/lib/isPostalCode"
import orgnrValidator from "se-orgnr-validator"
import getInputValidationClasses from "../../../lib/helpers/getInputValidationClasses"
import { InputDescription } from "../../common/form/input"
import { input } from "../../common/form/input.module.css"
import InputGroup from "../../common/form/inputGroup"
import Label from "../../common/form/label"
import { ErrorMessage } from "../errorMessage"
import useCheckoutContext, { useUserDetails } from "../store"
import SubmitStepButton from "./submitStepButton"
import Personnummer from "personnummer"

function CompanyDetails() {
  const { setNextActiveStep } = useCheckoutContext()
  const { userDetails, setUserDetails } = useUserDetails()
  const methods = useForm({
    defaultValues: {
      companyName: userDetails?.companyName || "",
      orgNo: userDetails?.orgNo || "",
      firstName: userDetails?.firstName || "",
      lastName: userDetails?.lastName || "",
      phone: userDetails?.phone || "",
      email: userDetails?.email || "",
      terms: userDetails?.invoiceInfo || false,
      InvoiceAddress: userDetails?.InvoiceAddress,
      InvoiceCity: userDetails?.InvoiceCity,
      InvoicePostNumber: userDetails?.InvoicePostNumber,
      InvoiceEmail: userDetails?.InvoiceEmail
    },
    mode: "onTouched"
  })

  const { register, handleSubmit, control } = methods

  const onSubmit = (values) => {
    setUserDetails(values)
    setNextActiveStep()
  }

  function isOrgnrValid(value) {
    if (orgnrValidator(value) || Personnummer.valid(value)) {
      return true
    }
    return false
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-wrap -mx-3">
          <div className="flex flex-col w-full px-3 mb-3 lg:mb-6">
            <EditDetail name="companyName" label="Företagsnamn" />
          </div>

          <div className="flex flex-col w-full px-3 mb-3 lg:mb-6">
            <EditDetail
              name="orgNo"
              label="Organisationsnummer"
              validationRules={{
                required: true,
                validate: (value) =>
                  isOrgnrValid(value) ||
                  "Ange ett giltigt organisationsnummer. Ex. 9988771234 eller 998877-1234"
              }}
            />
          </div>

          <div className="flex flex-col w-full px-3 mb-3 sm:w-1/2 lg:mb-6">
            <EditDetail name="firstName" label="Kontaktperson - Förnamn" />
          </div>
          <div className="flex flex-col w-full px-3 mb-3 sm:w-1/2 lg:mb-6">
            <EditDetail name="lastName" label="Kontaktperson - Efternamn" />
          </div>

          <div className="w-full px-3 mb-3 sm:w-1/2">
            <InputGroup>
              <Label required={true} name="email">
                Kontaktperson - E-postadress
              </Label>
              <input
                className={[
                  input,
                  getInputValidationClasses("email", control)
                ].join(" ")}
                id="email"
                {...register("email", {
                  required: true,
                  validate: (value) =>
                    isEmail(value) || "Ange en giltig e-postadress"
                })}
              />
              <ErrorMessage name="email" />
              <InputDescription>
                Vi skickar en bekräftelse till dig
              </InputDescription>
            </InputGroup>
          </div>
          <div className="w-full px-3 mb-3 sm:w-1/2">
            <InputGroup>
              <Label name="phone">Kontaktperson - Telefonnummer</Label>
              <input
                id="phone"
                className={[
                  input,
                  getInputValidationClasses("phone", control)
                ].join(" ")}
                {...register("phone", {
                  validate: (value) =>
                    !value ||
                    isMobilePhone(value, "sv-SE") ||
                    "Ange ett giltigt mobiltelefonnummer."
                })}
                placeholder="Endast siffror, ex. 0700000000"
              />
              <ErrorMessage name="phone" />
            </InputGroup>
          </div>

          <div className="flex flex-col w-full px-3 mb-3 lg:mb-6">
            <EditDetail
              id="InvoiceEmail"
              name="InvoiceEmail"
              validationRules={{
                required: true,
                validate: (value) => {
                  if (value === "") return true
                  return isEmail(value) || "Ange en giltig e-postadress"
                }
              }}
              label="E-post för faktura"
            />
          </div>

          <div className="flex flex-col w-full px-3 mb-3 lg:mb-6">
            <EditDetail name="address" label="Fakturaadress" />
          </div>

          <div className="flex flex-col w-full px-3 mb-3 sm:w-1/2 lg:mb-6">
            <EditDetail
              name="zip"
              label="Postnummer"
              validationRules={{
                required: true,
                validate: (value) =>
                  isPostalCode(value, "SE") || "Ange ett giltigt postnummer."
              }}
            />
          </div>
          <div className="flex flex-col w-full px-3 mb-3 sm:w-1/2 lg:mb-6">
            <EditDetail name="city" label="Ort" />
          </div>
        </div>

        <SubmitStepButton />
      </form>
    </FormProvider>
  )
}

function EditDetail({
  name,
  label,
  description,
  validationRules = { required: true },
  ...props
}) {
  const { userDetails } = useUserDetails()

  const { register, control } = useFormContext()

  return (
    <>
      <Label required={validationRules.required} name={name}>
        {label}
      </Label>
      <input
        id={name}
        {...register(name, validationRules)}
        className={[input, getInputValidationClasses(name, control)].join(" ")}
        defaultValue={userDetails[name]}
        {...props}
      />
      <ErrorMessage name={name} />
      {description && <InputDescription>{description}</InputDescription>}
    </>
  )
}

export default CompanyDetails
